'use client';

import { Field } from '../../../components/field';
import { Input } from '../../../components/input';
import { useDraftOnOpen } from '../../application-general/useDraftOnOpen';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import {
  PROFILE_LIST_VALUE_ERROR,
  removeAtIndex,
  upsertAtIndex,
  validateProfileListValue,
  type ProfileListValueError,
} from './business-profile.model';
import { ProfileListCard } from './ProfileListCard';
import { useProfileListDialog } from './useProfileListDialog';

export type ProfileListEditorLabels = {
  emptyState: string;
  add: string;
  addTitle: string;
  editTitle: string;
  fieldLabel: string;
  fieldPlaceholder: string;
  validationRequired: string;
  validationDuplicate: string;
};

export type ProfileListEditorProps = {
  title: string;
  tooltip: string;
  values: string[];
  isReadOnly: boolean;
  isSaving: boolean;
  onSave: (values: string[]) => Promise<boolean>;
  labels: ProfileListEditorLabels;
};

type ProfileListDraft = {
  value: string;
  error: ProfileListValueError | null;
};

export function ProfileListEditor({
  title,
  tooltip,
  values,
  isReadOnly,
  isSaving,
  onSave,
  labels,
}: ProfileListEditorProps) {
  const dialog = useProfileListDialog();
  const [draft, setDraft] = useDraftOnOpen<ProfileListDraft>({
    open: dialog.isOpen,
    value: {
      value: dialog.editIndex === null ? '' : (values[dialog.editIndex] ?? ''),
      error: null,
    },
  });
  const errorLabels: Record<ProfileListValueError, string> = {
    [PROFILE_LIST_VALUE_ERROR.REQUIRED]: labels.validationRequired,
    [PROFILE_LIST_VALUE_ERROR.DUPLICATE]: labels.validationDuplicate,
  };

  const handleSubmit = async () => {
    const error = validateProfileListValue({
      values,
      draft: draft.value,
      editIndex: dialog.editIndex,
    });

    if (error) {
      setDraft((previous) => ({ ...previous, error }));

      return;
    }

    const saved = await onSave(
      upsertAtIndex({
        values,
        value: draft.value.trim(),
        editIndex: dialog.editIndex,
      }),
    );

    if (saved) {
      dialog.close();
    }
  };

  return (
    <ProfileListCard
      title={title}
      tooltip={tooltip}
      items={values.map((value, index) => ({
        key: `${index}-${value}`,
        label: value,
      }))}
      emptyLabel={title}
      emptyValue={labels.emptyState}
      addLabel={labels.add}
      isReadOnly={isReadOnly}
      isSaving={isSaving}
      onAdd={dialog.openCreate}
      onEdit={dialog.openEdit}
      onDelete={(index) => void onSave(removeAtIndex({ values, index }))}
    >
      <SettingsEditDialog
        open={dialog.isOpen}
        title={dialog.isCreate ? labels.addTitle : labels.editTitle}
        onOpenChange={(open) => {
          if (!open) {
            dialog.close();
          }
        }}
        onSubmit={() => void handleSubmit()}
        loading={isSaving}
      >
        <Field.Root invalid={draft.error !== null}>
          <Field.Label>{labels.fieldLabel}</Field.Label>
          <Input
            value={draft.value}
            onChange={(event) =>
              setDraft({ value: event.target.value, error: null })
            }
            placeholder={labels.fieldPlaceholder}
          />
          {draft.error ? (
            <Field.ErrorText>{errorLabels[draft.error]}</Field.ErrorText>
          ) : null}
        </Field.Root>
      </SettingsEditDialog>
    </ProfileListCard>
  );
}
